/**
 * The Search page's query model — the state the controls edit, and its translation into the DSL.
 *
 * Extracted from SearchPage so that the free-text parser can PRODUCE this shape without
 * importing the page (and so the page cannot import the parser's internals). One model, two
 * writers: the filter controls and `queryParser`. Anything the parser can express, a human can
 * see and correct in the controls, because both edit the same object.
 *
 * ─── THE QUESTION IS PART OF THE QUERY ───
 * The page began with filters alone, and every query it could build ended in `aggregate: 'list'`
 * — a page of detections. But an operator asking "which areas did they visit" or "did everyone
 * wear a helmet" is not asking for detections; they are asking for a different SHAPE of answer
 * over the same filter. So `question` sits alongside the filters and decides the aggregate.
 * Making a human read a list of two hundred detections to work out which rooms appear in it is
 * how a search feature goes unused.
 */
import type {
  SearchAggregate, SearchDsl, SearchFuzziness, SearchPlaceBy, SearchSort, SearchSubject,
  SearchTextClause, SearchTextField, SearchMatchGroup, SearchEventType,
} from '../../api/contracts';

// ─── Vocabulary → operator-facing words ────────────────────────────────────

export const FIELD_LABEL: Record<string, string> = {
  // Person
  upper_color: 'Upper',
  lower_color: 'Lower',
  gender: 'Gender',
  age_band: 'Age',
  accessories: 'Carrying',
  // Vehicle
  vehicle_type: 'Type',
  vehicle_color: 'Colour',
  direction: 'Direction',
};

/** Dimensions rendered as a swatch grid rather than a list — every colour field. */
export const COLOR_FIELDS = new Set(['upper_color', 'lower_color', 'vehicle_color']);

/**
 * The window chips. `all_time` is offered only where the node advertises it (see
 * `timePresetsFor`) — an older node rejects an unknown preset with a 400.
 */
export const TIME_PRESETS: Array<{ value: string; label: string }> = [
  { value: 'all_time', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
];

export const MODEL_LABEL: Record<string, string> = {
  person: 'Person detection', face: 'Face recognition', person_count: 'People counting',
  intrusion: 'Intrusion', vehicle_crossing: 'Vehicle', ppe_violation: 'PPE',
  theft: 'Theft', fall: 'Fall', fire_smoke: 'Fire / Smoke', ocr: 'Text & plates (OCR)', other: 'Other',
};

/**
 * The same models in one or two words, for places that have a corner rather than a line.
 *
 * A search result card is a camera frame with its facts written over it, and "Person detection +
 * Face recognition" does not fit in the corner of one — it truncates to "Person detect…", which
 * reads as a broken label rather than a short one. Kept beside MODEL_LABEL so a new model gets
 * both spellings in the same edit, and falls back to the long one where it is already short.
 */
export const MODEL_SHORT: Record<string, string> = {
  person: 'Person', face: 'Face', person_count: 'Count',
  intrusion: 'Intrusion', vehicle_crossing: 'Vehicle', ppe_violation: 'PPE',
  theft: 'Theft', fall: 'Fall', fire_smoke: 'Fire', ocr: 'OCR', other: 'Other',
};

/**
 * Event types, bucketed the way an operator thinks about them rather than the way the tables are
 * named. The headings are also what makes "people or vehicles?" a one-glance decision.
 */
export const MODEL_GROUP: Record<string, string> = {
  person: 'People', face: 'People', person_count: 'People',
  vehicle_crossing: 'Vehicles', ocr: 'Vehicles',
  intrusion: 'Safety & security', ppe_violation: 'Safety & security',
  fall: 'Safety & security', fire_smoke: 'Safety & security', theft: 'Safety & security',
  other: 'Other',
};

/** Model keys that belong to neither subject — reachable through the "Other types" control. */
export const SUBJECT_MODELS = new Set(['person', 'face', 'vehicle_crossing', 'ocr']);

/**
 * The models whose events earn a marker pin on the event timeline rather than a bar.
 *
 * A count cannot say that two of an hour's ninety events were a fire, and a shared bar scale
 * rounds two events next to nine hundred to nothing — so these are drawn individually, at their
 * real time, in severity colour.
 *
 * Scoped by MODEL rather than by severity because the DSL has no severity filter: `severity` is
 * a column on the row, so it can colour a pin, but it cannot narrow the query that fetches one.
 * These four are the models the node treats as critical.
 */
export const MARKER_MODELS = ['fire_smoke', 'intrusion', 'theft', 'fall'];

/**
 * Which marker models this query is actually searching — never any others.
 *
 * The intersection matters: pinning a fire onto the timeline of a "person in a red shirt" search
 * would put events on the range control that are not in the answer it controls, which reads as
 * the chart and the results disagreeing. Empty means no rail and no request.
 */
export function markerModelsFor(f: Filters, subjectDef: SubjectDef): string[] {
  const scope = new Set([...modelsFor(f, subjectDef), ...f.otherModels]);
  return MARKER_MODELS.filter((m) => scope.has(m));
}

/**
 * The picture of THIS subject — the key inside `image_urls`, not a URL.
 *
 * A frame of six people is six result cards, and before the projection identified each person's
 * own crop all six showed the same wide shot: six identical pictures under six different
 * descriptions, which reads as the page repeating itself rather than as six people.
 *
 * Returns the row's own crop where the module sent one, and null otherwise — the caller then
 * falls back to the full frame, which is what every card showed before and is still the right
 * answer for a subjectless event or a model that ships no crops.
 *
 * A KEY rather than a resolved URL because the two hosts need different things from it. The local
 * page reads `image_urls[key]` directly; the cloud page cannot — those URLs point inside the
 * node's network — and passes the key to the tunnel as the image type to stream back.
 */
export function subjectCropKey(row: { crop_key?: string | null; image_urls?: Record<string, string> } | null | undefined): string | null {
  const key = row?.crop_key;
  if (!key) return null;
  // Guarded against a row whose crop was pruned from the map since it was projected: naming an
  // image that is not there would render a broken <img> where a full frame was available.
  return row?.image_urls?.[key] ? key : null;
}

/** The URL of this subject's own crop, else the whole frame. For a host that can load either. */
export function subjectImageUrl(row: { crop_key?: string | null; image_urls?: Record<string, string> } | null | undefined): string | null {
  const urls = row?.image_urls;
  if (!urls || typeof urls !== 'object') return null;
  const crop = subjectCropKey(row);
  if (crop) return urls[crop];
  // An OCR event ships two frames; the card shows the ANNOTATED one (boxes drawn round what was
  // read), the plain frame only when that is missing. Named before the any-image fallback, which
  // would otherwise pick whichever came first.
  const frame = urls.fullFrame || urls.full_frame || urls.fullframe || urls.full || urls.frame
    || urls.ocr_annotated_frame || urls.ocr_full_frame;
  if (frame) return frame;
  const any = Object.values(urls).find((u) => typeof u === 'string' && u);
  return (any as string) || null;
}

/**
 * One attribute VALUE, as a word.
 *
 * NOT `titleCase`, which turns a hyphen into a space and renders the age band `18-29` as
 * "18 29" — two numbers where there was a range. Underscores do become spaces (they are a key
 * separator, never part of a value), and only the first letter is raised, so digits and hyphens
 * come through as they were stored.
 *
 * Lives here rather than beside its first caller because it is a rule about the VOCABULARY, and
 * the vocabulary is what this file is. Two copies would be two chances to reach for `titleCase`
 * on a value and break the age band in one place only.
 */
export const attrWord = (v: string) => {
  const w = v.replace(/_+/g, ' ');
  return w.charAt(0).toUpperCase() + w.slice(1);
};

export const titleCase = (v: string) => v.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ─── Subjects ──────────────────────────────────────────────────────────────

/**
 * `all` is the third page: every model at once, with no attribute row, searched by TEXT — a name,
 * a plate, or a word the OCR model read. It is a subject so it can reuse the page whole: its
 * definition simply has no fields and an empty default (no model filter).
 */
export type SubjectKey = 'person' | 'vehicle' | 'all';

/**
 * The fallbacks for a node whose `/search/schema` call failed.
 *
 * A usable page with the fields this build knows about beats an empty filter bar that looks
 * broken. The live mapping comes from the API, so adding a field on one subject needs no edit
 * here.
 */
export const SUBJECT_FALLBACK: Record<SubjectKey, Required<Pick<SearchSubject, 'label' | 'models' | 'defaultModels' | 'identityModels' | 'fields' | 'textFilter' | 'textFields'>>> = {
  person: {
    label: 'Person',
    models: ['person', 'face'],
    // Narrower than `models` on purpose — see SearchSubject.defaultModels.
    defaultModels: ['person'],
    // The only model that carries a name — see SearchSubject.identityModels.
    identityModels: ['face'],
    fields: ['upper_color', 'lower_color', 'gender', 'age_band', 'accessories'],
    textFilter: 'person',
    textFields: ['name'],
  },
  vehicle: {
    label: 'Vehicle',
    // OCR plate reads are vehicles too — offerable, joined automatically by a plate search.
    models: ['vehicle_crossing', 'ocr'],
    defaultModels: ['vehicle_crossing'],
    identityModels: ['vehicle_crossing', 'ocr'],
    fields: ['vehicle_type', 'vehicle_color', 'direction'],
    textFilter: 'plate',
    textFields: ['plate'],
  },
  all: {
    label: 'All',
    models: ['person', 'face', 'vehicle_crossing', 'ocr', 'intrusion', 'ppe_violation', 'theft',
      'fall', 'fire_smoke', 'person_count', 'other'],
    defaultModels: [],
    identityModels: [],
    fields: [],
    textFilter: null,
    textFields: ['name', 'plate', 'text'],
  },
};

export type SubjectDef = {
  label: string;
  models: string[];
  defaultModels: string[];
  /** The models that can match an identity. See SearchSubject.identityModels. */
  identityModels: string[];
  fields: string[];
  textFilter?: string | null;
  /** Which text-index fields the page's free text searches. */
  textFields: SearchTextField[];
};

/** The subject's definition from the API where there is one, else this build's fallback. */
export function resolveSubjectDef(
  subjects: Record<string, SearchSubject> | undefined,
  key: SubjectKey,
): SubjectDef {
  const api = subjects?.[key];
  if (!api) return SUBJECT_FALLBACK[key];
  return {
    label: api.label,
    models: api.models,
    // An older node's schema has no defaultModels. Falling back to the full set keeps ITS
    // behaviour rather than silently narrowing a search against a server that never agreed to it.
    // `all` is the exception: its default is deliberately EMPTY (no model filter at all).
    defaultModels: key === 'all' ? (api.defaultModels || []) : (api.defaultModels?.length ? api.defaultModels : api.models),
    /**
     * `identityModels` falls back to THIS BUILD's knowledge, not to the node's model list — the
     * opposite of the line above, and deliberately so.
     *
     * Which model carries a name is a property of the schema (person_events has no identity
     * column on any node version), not a policy the node chooses. An older node that does not
     * publish the field stores names in exactly the same place, so using what we know finds the
     * events; falling back to `api.models` would union person detection into an identity search,
     * which is the double-count `defaultModels` exists to prevent.
     */
    identityModels: (api.identityModels?.length ? api.identityModels : SUBJECT_FALLBACK[key].identityModels)
      .filter((m) => api.models.includes(m)),
    fields: api.fields,
    textFilter: api.textFilter,
    textFields: api.textFields?.length ? api.textFields : SUBJECT_FALLBACK[key].textFields,
  };
}

// ─── Questions ─────────────────────────────────────────────────────────────

export type QuestionKey = 'list' | 'when' | 'where' | 'verdict' | 'journey' | 'compliance';

export interface QuestionSpec {
  key: QuestionKey;
  /** The button text — a question, not a noun, because that is what the operator is choosing. */
  label: string;
  /** What the answer will be, in one line, shown beside the control. */
  help: string;
  aggregate: SearchAggregate;
  /** Returns rows the operator pages through. The other shapes are bounded by construction. */
  paged?: boolean;
  /** Meaningless without a place in scope, and the page says so instead of answering. */
  needsPlace?: boolean;
  /** Needs a value to divide the matching people by. */
  needsSplit?: boolean;
}

/**
 * The six answer shapes, ordered from the broadest to the most specific question.
 *
 * `list` stays first and stays the default: it is what the page did before this control existed,
 * so an operator who ignores the control gets exactly the behaviour they already know.
 */
export const QUESTIONS: QuestionSpec[] = [
  {
    key: 'list',
    label: 'Show me',
    help: 'Every matching detection, newest first.',
    aggregate: 'list',
    paged: true,
  },
  {
    key: 'when',
    label: 'When?',
    help: 'How the matches spread across the window, and the first and last sighting.',
    aggregate: 'timeline',
  },
  {
    key: 'where',
    label: 'Which areas?',
    help: 'The places the matches happened in, busiest first.',
    aggregate: 'by_place',
  },
  {
    key: 'verdict',
    label: 'Was it there?',
    help: 'Yes or no for the place you pick, with the evidence.',
    aggregate: 'by_place',
    needsPlace: true,
  },
  {
    key: 'journey',
    label: 'Journey',
    help: 'Each person\'s trail through the estate, in the order they moved.',
    aggregate: 'journey',
    paged: true,
  },
  {
    key: 'compliance',
    label: 'Did all of them…?',
    help: 'How many people had it — and, by name, who did not.',
    aggregate: 'attribute_split',
    needsSplit: true,
  },
];

export const QUESTION_BY_KEY: Record<QuestionKey, QuestionSpec> = Object.fromEntries(
  QUESTIONS.map((q) => [q.key, q]),
) as Record<QuestionKey, QuestionSpec>;

// ─── Filter state ──────────────────────────────────────────────────────────

/**
 * The search window. Neither a preset nor from/to means THE DEFAULT window — all time on a node
 * that offers it, today on one that does not (`defaultTimePreset`). Kept unresolved in the filters
 * because the answer depends on the node's schema, which arrives after the page's first render
 * and differs between the sites a cloud operator switches between.
 */
export interface TimeValue { preset?: string; from?: string; to?: string }

/** What an unset window means on this node — see TimeValue. */
export function defaultTimePreset(opts: { allTime?: boolean } = {}): string {
  return opts.allTime ? 'all_time' : 'today';
}

/** The window chips this node accepts. */
export function timePresetsFor(opts: { allTime?: boolean } = {}) {
  return opts.allTime ? TIME_PRESETS : TIME_PRESETS.filter((p) => p.value !== 'all_time');
}

/** A window was CHOSEN (rather than left at the default) — it gets an × and counts as a filter. */
export function timeChosen(t: TimeValue): boolean {
  return Boolean(t.preset || t.from);
}

export interface Filters {
  /** The answer shape. Decides the aggregate and which controls are load-bearing. */
  question: QuestionKey;
  time: TimeValue;
  /**
   * A sub-range of `time`, dragged out on the event timeline. ISO strings; null = the whole window.
   *
   * A SECOND time field rather than an overwrite of the first, and that is the whole point. The
   * strip has to keep drawing the full window while a narrow slice of it is selected — otherwise
   * selecting 14:00–15:00 would redraw the chart as 14:00–15:00, there would be nothing left to
   * drag against, and the operator could not widen back out or see that the hour they picked was
   * the quiet one. So `time` stays the window the bars span, `range` is the slice the answer is
   * about, and `toDsl` sends whichever is narrower.
   *
   * Cleared whenever `time` changes: a range inside yesterday is meaningless once the window
   * moves to last month, and silently keeping it would answer a question nobody asked.
   */
  range: { from: string; to: string } | null;
  groups: string[];
  cameras: string[];
  /** The subject being searched. Decides which attribute row is shown and which models apply. */
  subject: SubjectKey;
  /**
   * Which of the subject's models to search — the "Detected by" control.
   *
   * Empty means "the subject's defaults", never "every model on the node": an empty `models` in
   * the DSL is the server's wildcard, so sending one would silently widen the search to fires and
   * vehicle crossings.
   */
  subjectModels: string[];
  /**
   * Models OUTSIDE the two subjects — intrusion, fire, fall, PPE, theft, people-counting.
   *
   * A separate dimension rather than a value of `subject`, so choosing "fire events" never
   * silently discards the attribute filters the operator had set: they are simply not applied to
   * models that cannot carry them. Nothing on this node becomes unsearchable.
   */
  otherModels: string[];
  /** field → chosen values. Several values for one field mean "any of". */
  attributes: Record<string, string[]>;
  /** null = either; true = known people only; false = unknown only. Person subject only. */
  known: boolean | null;
  /**
   * Which kind of OCR event, when OCR is picked as a type: events where any number plate was read
   * ('plate', Identity "GJ05F2150") or events with only text ('text_only', "Text in frame").
   * Set from the type list's two OCR entries. Optional so every Filters literal stays valid.
   */
  ocrRead?: 'any' | 'plate' | 'text_only';
  /**
   * A person's NAME, prefix-matched — the person subject's equivalent of `plate`.
   *
   * Finds anyone the registry can name, which since migration 032 means employees AND
   * registered non-employees: one `persons` row per human, and the projection stores whichever
   * name resolved into `subject_label`. Asking "where was Asha today" must not depend on
   * whether somebody got round to marking her staff.
   *
   * Separate from `known`, which is the boolean "is this anyone we have enrolled". The two
   * compose: a name with `known: null` is the normal case, since naming someone already
   * implies they are enrolled.
   */
  personName: string;
  /** Vehicle plate, prefix-matched. Vehicle subject only. */
  plate: string;
  /**
   * Whether the subject is on a WATCH LIST — blacklisted, wanted, missing, stolen.
   *
   * Both subjects, unlike `known`: a plate is flagged in `vehicle_blacklist`, a person in
   * `persons.is_blacklisted`, and "show me the flagged ones" is the same question either way.
   *
   * RESOLVED AGAINST THE LIST AS IT STANDS, not against a verdict recorded on the event — see
   * the server's own note. Somebody is flagged BECAUSE of something, and the first question
   * afterwards is where they have been; a filter tied to the ingest verdict would answer that
   * with nothing, because none of those sightings were flagged when they happened.
   *
   * `'not_listed'` is a real question, not the absence of one: clearing a shift means finding
   * everyone who is NOT on a list.
   */
  watchlist: 'any' | 'listed' | 'not_listed';
  /**
   * Which listings count, when `watchlist` is `listed`. Empty = any of them.
   *
   * Open strings rather than a closed union: the vocabulary lives in the watch-list screens
   * (blacklisted, wanted, missing for people; blacklisted, stolen for vehicles) and a search
   * must not go stale the day a category is added.
   */
  watchlistCategories: string[];
  /** How the "Which areas?" answer buckets its places. */
  placeBy: SearchPlaceBy;
  /** What the compliance question divides people by — e.g. accessories = helmet. */
  splitBy: { field: string; value: string } | null;
  /**
   * Free text, matched FUZZILY against names, plates and the words OCR read — whichever of those
   * the subject searches (`SubjectDef.textFields`). Words the sentence parser could not bind to a
   * filter land here, so "badal near dethali" searches for both words rather than dropping them.
   */
  text: string;
  /** Appearance words the last sentence also looks for in OCR text (`or_text`). Sentence-owned. */
  orText: string;
  /** How far a spelling may stray from what was typed. Applies to name, plate and text alike. */
  fuzziness: SearchFuzziness;
  /**
   * Spellings the operator struck out of the fuzzy match — "badal, but not kajal". Cleared
   * whenever the words they belong to change: a strike-out is about one search's spellings.
   */
  excludedTerms: string[];
  /**
   * THE ONE-BOX SEARCH — every typed word with everything it could mean (`parseMatch`). Owned by
   * the sentence: a new sentence replaces it, and the "Understood" chips can drop one reading.
   * Sent only to a node whose schema advertises `match`.
   */
  match: SearchMatchGroup[];
  /** Refinement: event types the operator removed from the results (`hide_types`). */
  hiddenTypes: SearchEventType[];
  /**
   * Spellings the operator ADDED to a typed word — "rajan", and also "raj" and "tajan" — keyed by
   * the word. Searched as exact whole words, OR'd with the word itself, everywhere it is searched.
   * Cleared with the strike-outs when the sentence's words change.
   */
  addedSpellings: Record<string, string[]>;
  /** Typed words searched with NO spelling tolerance, whatever the page's level is. */
  exactWords: string[];
}

export const EMPTY_FILTERS: Filters = {
  question: 'list',
  time: {},
  range: null,
  groups: [], cameras: [], subject: 'person', subjectModels: [], otherModels: [],
  attributes: {}, known: null, personName: '', plate: '',
  watchlist: 'any', watchlistCategories: [],
  placeBy: 'group',
  splitBy: null,
  text: '', fuzziness: 'loose', excludedTerms: [], orText: '',
  match: [], hiddenTypes: [], addedSpellings: {}, exactWords: [],
};

// ─── The typed sentence as a LAYER over the filters ────────────────────────
//
// The query bar used to REPLACE the filters: every sentence was applied over an empty filter
// set. So a camera picked in the panel vanished the moment the sentence was edited (and with
// apply-as-you-type, a single keystroke edits it), and a second sentence threw away the first
// one's filters. Only the latest action ever survived.
//
// Now the sentence is a layer. The page remembers what the LAST sentence contributed; applying a
// new one first takes that contribution back out (only where it is still as the sentence left
// it — a value changed by hand since is the operator's now) and then lays the new one on top.
// Everything else, including every filter picked by hand, stays.

/** Dimensions where a sentence ADDS to the operator's picks rather than replacing them. */
const LAYER_LIST_KEYS = ['cameras', 'groups', 'otherModels', 'watchlistCategories'] as const;

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Take a sentence's contribution back out of the filters.
 *
 * Lists lose exactly the items the sentence added; attributes lose exactly the values it added;
 * anything else is reset only if it still holds the value the sentence put there. The SUBJECT is
 * never reset — it is where the operator is working, not a filter the sentence owns.
 */
export function withoutSentence(f: Filters, layer: Partial<Filters> | null, empty: Filters): Filters {
  if (!layer) return f;
  const out: Filters = { ...f, attributes: { ...f.attributes } };
  for (const key of Object.keys(layer) as Array<keyof Filters>) {
    if (key === 'subject') continue;
    if ((LAYER_LIST_KEYS as readonly string[]).includes(key)) {
      const added = new Set((layer[key] as string[]) || []);
      (out as any)[key] = ((f[key] as string[]) || []).filter((x) => !added.has(x));
    } else if (key === 'attributes') {
      for (const [field, values] of Object.entries(layer.attributes || {})) {
        const left = (f.attributes[field] || []).filter((v) => !values.includes(v));
        if (left.length) out.attributes[field] = left; else delete out.attributes[field];
      }
    } else if (sameValue(f[key], layer[key])) {
      (out as any)[key] = empty[key];
      if (key === 'time') out.range = null;
    }
  }
  return out;
}

/**
 * Lay a sentence over the filters.
 *
 * Places and models ADD ("at gate 1" on top of a camera picked by hand searches both, as picking
 * both in the panel would). Everything else the sentence names REPLACES that one dimension —
 * "yesterday" is the window, "blue shirt" is the shirt colour — and every dimension it does not
 * name is left exactly as it was.
 */
export function withSentence(f: Filters, layer: Partial<Filters>): Filters {
  const out: Filters = { ...f, ...layer, attributes: { ...f.attributes, ...(layer.attributes || {}) } };
  for (const key of LAYER_LIST_KEYS) {
    if (key === 'watchlistCategories' || !layer[key]) continue;
    (out as any)[key] = [...new Set<string>([...(f[key] || []), ...(layer[key] || [])])];
  }
  // Categories narrow "listed" — a sentence that sets the watch list sets them with it.
  if (layer.watchlist) out.watchlistCategories = layer.watchlistCategories || [];
  // A dragged range is a slice of the OLD window.
  if (layer.time) out.range = null;
  // Struck-out spellings belong to the words that produced them.
  if (['text', 'personName', 'plate', 'match'].some((k) => k in layer && !sameValue((layer as any)[k], (f as any)[k]))) {
    out.excludedTerms = [];
    out.addedSpellings = {};
    out.exactWords = [];
  }
  return out;
}

/** Enough letters or digits to be a word the server will accept (it refuses "a" or "14:05"). */
export function searchableText(q: string): boolean {
  return q.replace(/\b\d{1,2}:\d{2}(:\d{2})?\b/g, ' ').replace(/[^\p{L}\p{N}]+/gu, ' ')
    .split(' ').some((w) => w.length >= 2);
}

/** Is this query asking about a specific identity — a name, a plate, or "enrolled only"? */
export function identityFiltered(f: Filters, subjectDef: SubjectDef): boolean {
  // Free text on a subject page searches that subject's identity field (name / plate), so it
  // widens the models exactly as the name and plate boxes do.
  const text = Boolean(f.text.trim());
  if (subjectDef.textFilter === 'plate') return Boolean(f.plate.trim()) || text;
  if (subjectDef.textFilter !== 'person') return false;
  // `known: false` is NOT an identity filter. "Unidentified only" is exactly what a person
  // detection is, so it matches the default models perfectly well and needs no widening.
  return Boolean(f.personName.trim()) || f.known === true || text;
}

/**
 * The models a query will actually search, for the active subject.
 *
 * AN IDENTITY FILTER WIDENS THE DEFAULT, and this is the one place that happens.
 *
 * The person subject starts on person detection alone, to stop one human being counted twice
 * when both detectors are attached. But `person_events` holds no identity — no matched id, no
 * name — so a search for "Asha", or for enrolled people, ran against rows that structurally
 * could not match and came back empty. Not a narrow answer: a WRONG one, "she was not here",
 * about somebody the face model had recorded all day. Every operator who typed a name into the
 * page got that, and the fix was a control they had no way of knowing they had to find.
 *
 * Widening is safe for exactly the reason the default is narrow: the identity filter itself
 * excludes every person-detection row (their `subject_label` is NULL, their `is_known` false),
 * so unioning face in adds the only rows that can match and duplicates nothing.
 *
 * An EXPLICIT choice still wins, including one that makes the search unanswerable — the operator
 * is told (`identityUnsearchable`) rather than overruled.
 */
export function modelsFor(f: Filters, subjectDef: SubjectDef): string[] {
  const chosen = f.subjectModels.filter((m) => subjectDef.models.includes(m));
  if (chosen.length) return chosen;
  if (!identityFiltered(f, subjectDef)) return subjectDef.defaultModels;
  return [...new Set([...subjectDef.defaultModels, ...subjectDef.identityModels])];
}

/**
 * The models an identity filter ADDED to the defaults, or none.
 *
 * The page says this out loud. A search that quietly searches more than the control claims is
 * the same failure as one that quietly searches less: the "Detected by" summary would read
 * "Person detection" while face events sat in the results.
 */
export function identityAddedModels(f: Filters, subjectDef: SubjectDef): string[] {
  if (f.subjectModels.length || !identityFiltered(f, subjectDef)) return [];
  return subjectDef.identityModels.filter((m) => !subjectDef.defaultModels.includes(m));
}

/**
 * True when the operator has hand-picked models that cannot carry an identity while filtering by
 * one — a search that is guaranteed to return nothing.
 *
 * Worth saying because the empty result is indistinguishable from "they were not there", which
 * is the failure this whole widening exists to end. Their choice is not overridden; it is named.
 */
export function identityUnsearchable(f: Filters, subjectDef: SubjectDef): boolean {
  if (!f.subjectModels.length || !identityFiltered(f, subjectDef)) return false;
  if (!subjectDef.identityModels.length) return false;
  return !f.subjectModels.some((m) => subjectDef.identityModels.includes(m));
}

/** Has the operator narrowed the search to somewhere? What "Was it there?" needs. */
export function hasPlace(f: Filters): boolean {
  return f.cameras.length > 0 || f.groups.length > 0;
}

/**
 * Filters → the DSL the API takes. The one place the UI's model becomes the server's.
 *
 * @param subjectDef the active subject's field list, so only ITS attributes are sent. A colour
 *   left selected on the Person tab must not travel with a Vehicle query — the server would
 *   accept it (the field is valid) and correctly return nothing, which would look like a bug.
 */
export interface DslOptions {
  /**
   * The node can do fuzzy text search (its schema has `text_search`). Without it the name and
   * plate go out as the old prefix filters, and free text is not sent at all — an older node
   * rejects an unknown `text` field with a 400, which would break the whole page.
   */
  textSearch?: boolean;
  /** The node's schema advertises `ocr_read`; an older node rejects the field. */
  ocrRead?: boolean;
  /** The node's schema advertises `or_text`; an older node rejects the field. */
  orText?: boolean;
  /**
   * The node's schema lists the `all_time` preset — the default window then. An older node
   * rejects it, so there the default stays today.
   */
  allTime?: boolean;
  /**
   * The operator's "OCR text" choice. False drops OCR TEXT rows (words read off boards) from a
   * text search on All — sent as `ocr_read: 'plate'`, which keeps OCR plate reads, because those
   * are vehicle sightings, not text. Default true.
   */
  ocrTextRows?: boolean;
  /** Row order. Track mode sends `newest`: its timeline folds CONSECUTIVE detections into stops. */
  sort?: SearchSort;
  /**
   * The node takes the one-box search (`match`, `kinds`). The words then travel as `match` and
   * nothing goes out as `or_text` — every colour word already carries its own text reading.
   */
  match?: boolean;
}

/**
 * The fuzzy text clauses for this query — name box, plate box and free text, one clause each.
 *
 * Separate clauses rather than one joined string, because each searches a different field: the
 * name box must not match a plate, and the free text on the All page searches everything.
 * Struck-out spellings ride on every clause; a spelling belongs to whichever clause produced it,
 * and striking it from the others is harmless (they never matched it).
 */
export function textClauses(f: Filters, subjectDef: SubjectDef): SearchTextClause[] {
  const exclude = f.excludedTerms.length ? f.excludedTerms : undefined;
  const clause = (q: string, fields: SearchTextField[]): SearchTextClause | null => (
    searchableText(q) ? { q: q.trim(), fields, fuzziness: f.fuzziness, ...(exclude ? { exclude } : {}) } : null
  );
  const out: Array<SearchTextClause | null> = [];
  if (subjectDef.textFilter === 'person' && f.personName.trim()) out.push(clause(f.personName, ['name']));
  if (subjectDef.textFilter === 'plate' && f.plate.trim()) out.push(clause(f.plate, ['plate']));
  if (f.text.trim()) out.push(clause(f.text, subjectDef.textFields));
  return out.filter((c): c is SearchTextClause => Boolean(c));
}

export function toDsl(
  f: Filters,
  subjectDef: SubjectDef,
  limit: number,
  offset: number,
  opts: DslOptions = {},
): SearchDsl {
  const spec = QUESTION_BY_KEY[f.question] || QUESTION_BY_KEY.list;
  const dsl: SearchDsl = { aggregate: spec.aggregate, limit, offset };

  // A dragged range wins over the window it was dragged inside — it is strictly narrower, and
  // sending both is not something the DSL allows (a preset and from/to are mutually exclusive).
  dsl.time = f.range
    ? { from: f.range.from, to: f.range.to }
    : f.time.from && f.time.to
      ? { from: new Date(f.time.from).toISOString(), to: new Date(f.time.to).toISOString() }
      : { preset: f.time.preset || defaultTimePreset(opts) };

  if (f.cameras.length) dsl.cameras = f.cameras;
  if (f.groups.length) dsl.groups = f.groups;

  // The subject's chosen models, plus any "other" types explicitly added. Sending both is what
  // keeps fire and intrusion events reachable while a subject is selected.
  let models = [...modelsFor(f, subjectDef), ...f.otherModels];
  // OCR arrived in the same node release as text search, so a node without text search has no
  // `ocr` model and would 400 on it. Only reachable through this build's fallback subjects (the
  // node's schema failed to load), but a fallback that breaks the page defeats its purpose.
  if (!opts.textSearch) models = models.filter((m) => m !== 'ocr');
  if (models.length) dsl.models = [...new Set(models)];
  // Only when OCR is actually being searched (no model list = every model, OCR included).
  if (opts.ocrRead && f.ocrRead && f.ocrRead !== 'any' && (!dsl.models || dsl.models.includes('ocr'))) {
    dsl.ocr_read = f.ocrRead;
  } else if (opts.ocrRead && opts.ocrTextRows === false && f.subject === 'all' && f.text.trim()
    && (!dsl.models || dsl.models.includes('ocr'))) {
    // "OCR text" switched off: a word or number searched on All keeps the plate reads only.
    dsl.ocr_read = 'plate';
  }

  // Only the attribute fields actually applied — see liveAttributeFields.
  const attributes: Record<string, string[]> = {};
  for (const field of liveAttributeFields(f, subjectDef)) {
    const values = f.attributes[field];
    if (values && values.length) attributes[field] = values;
  }

  // The sentence's appearance words, ALSO looked for in OCR text ("red shirt" on a board) — an OR
  // alongside the rest, list answers only. Sent only while the appearance filters it came from
  // are still applied: remove the "red" chip and the board stops being part of the question too.
  if (!opts.match && opts.orText && f.orText.trim() && spec.aggregate === 'list' && Object.keys(attributes).length
    && searchableText(f.orText)) {
    dsl.or_text = f.orText.trim();
  }

  // The identity filter is subject-specific: a name for people, a plate for vehicles.
  if (subjectDef.textFilter === 'person' && f.known !== null) dsl.known = f.known;
  if (opts.textSearch) {
    // Fuzzy: through the node's text index. "badal" also finds "vadal", and the answer lists
    // every spelling it used so the operator can strike out the wrong ones.
    const clauses = textClauses(f, subjectDef);
    if (clauses.length) dsl.text = clauses;
    if (opts.sort) dsl.sort = opts.sort;
  } else {
    // An older node: the exact prefix filters it has always understood. Prefix-matched against
    // `subject_label`, over the idx_eu_subject_label_prefix functional index.
    if (subjectDef.textFilter === 'person' && f.personName.trim()) {
      dsl.person = { name: f.personName.trim() };
    }
    if (subjectDef.textFilter === 'plate' && f.plate.trim()) dsl.plate = f.plate.trim();
  }

  // Both subjects. Categories only travel with `listed` — the server rejects the other
  // combination, because "not listed, under these categories" cannot be satisfied by anything.
  if (f.watchlist !== 'any') {
    dsl.watchlist = f.watchlist === 'listed'
      ? { listed: true, ...(f.watchlistCategories.length ? { categories: f.watchlistCategories } : null) }
      : { listed: false };
  }

  if (f.question === 'where') {
    dsl.place_by = f.placeBy;
  } else if (f.question === 'verdict') {
    // Always by CAMERA, never by group. The scope filter has already narrowed the query to the
    // place in question, so the rows only have to say which cameras inside it saw anything —
    // and a group roll-up would add every ancestor area as a row, which reads as "it was also in
    // the building" and answers a question nobody asked.
    dsl.place_by = 'camera';
  } else if (f.question === 'journey') {
    // The trail's stops are cameras: 'group' is rejected by the validator because a camera's
    // ancestor list would emit the same stop at three levels of the tree.
    dsl.place_by = 'camera';
  }

  if (f.question === 'compliance' && f.splitBy) {
    dsl.split_by = f.splitBy;
    // The split field must NOT also be a filter — every event would land on one side and the
    // ratio would read 100%. The server rejects it with a 400; dropping it here means the
    // operator's other filters still apply and the answer is the one they asked for. The page
    // says which filter it set aside.
    delete attributes[f.splitBy.field];
  }

  if (Object.keys(attributes).length) dsl.attributes = attributes;

  if (opts.match) {
    // The spelling level (Exact / Close / Loose) applies to EVERY text reading — names, plates,
    // OCR words, and the colour or vehicle word on a sign (which the server keeps one step
    // stricter: only Loose lets "red" start "reduce").
    if (f.match.length) {
      // Struck-out spellings ride on every text reading — a spelling belongs to whichever word
      // produced it, and striking it from the others is harmless (they never matched it).
      const exclude = f.excludedTerms.length ? f.excludedTerms : undefined;
      dsl.match = f.match.map((g) => {
        // "Exact only" on this word overrides the page's level for every one of its lookups.
        const level = f.exactWords.includes(g.word) ? 'exact' : f.fuzziness;
        return {
          ...g,
          any: [
            ...g.any.map((a) => ('text' in a
              ? { ...a, ...(a.fuzziness ? null : { fuzziness: level }), ...(exclude ? { exclude } : null) }
              : a)),
            // The operator's own spellings — each the word exactly as they typed it.
            ...(f.addedSpellings[g.word] || []).map((text) => ({
              text, fuzziness: 'exact' as const, whole_word: true, ...(exclude ? { exclude } : null),
            })),
          ],
        };
      });
    }
    if (f.hiddenTypes.length) dsl.hide_types = f.hiddenTypes;
  }

  return dsl;
}

/** How many dimensions are narrowed — drives the "Clear all" affordance. */
/**
 * The attribute fields that are actually APPLIED. On a subject page, that subject's own fields —
 * a colour left selected on the Person tab is not part of a Vehicle query. On ALL, whatever the
 * sentence set: "person red" or "red bus" stays on All (so OCR text that reads the same words is
 * found beside it) and the appearance narrows the people or vehicles in the answer.
 */
export function liveAttributeFields(f: Filters, subjectDef: SubjectDef): string[] {
  return f.subject === 'all'
    ? Object.keys(f.attributes).filter((k) => (f.attributes[k] || []).length > 0)
    : subjectDef.fields;
}

export function activeCount(f: Filters, subjectDef: SubjectDef): number {
  const modelsNarrowed = f.subjectModels.length > 0
    && f.subjectModels.slice().sort().join() !== subjectDef.defaultModels.slice().sort().join();

  return f.groups.length + f.cameras.length + f.otherModels.length
    // Only the active subject's attributes count — the others are not being applied.
    + liveAttributeFields(f, subjectDef).reduce((n, field) => n + (f.attributes[field]?.length || 0), 0)
    + (f.known !== null ? 1 : 0)
    + (f.personName.trim() ? 1 : 0)
    + (f.plate.trim() ? 1 : 0)
    + (f.text.trim() ? 1 : 0)
    // The categories narrow the same dimension, so they are not counted twice.
    + (f.watchlist !== 'any' ? 1 : 0)
    + (timeChosen(f.time) ? 1 : 0)
    + (f.range ? 1 : 0)
    + f.hiddenTypes.length
    + (modelsNarrowed ? 1 : 0);
}

/**
 * The same query with the dragged range dropped — what the timeline strip's own bars are drawn
 * from.
 *
 * The strip must answer "where in this window is the activity", which means it follows every
 * OTHER filter (a colour, a camera, a model) but never its own selection. Feeding it the range
 * would make the chart the answer to the question it is meant to be asking.
 */
export function windowScope(f: Filters): Filters {
  const scope: Filters = { ...f, question: 'when', range: null, splitBy: null };

  // The one filter the strip must ALSO drop, to stay honest about being "the same filters as the
  // answer". A compliance question measuring helmets cannot also filter to helmets — `toDsl`
  // sets that filter aside for the answer — so applying it to the bars would draw a window the
  // answer was not computed over.
  if (f.question === 'compliance' && f.splitBy) {
    const { [f.splitBy.field]: _setAside, ...rest } = f.attributes;
    scope.attributes = rest;
  }
  return scope;
}

/**
 * The closed button's text for a multi-select attribute.
 *
 * Names the first value and counts the rest ("Red +2") rather than saying "3 selected". The
 * button is the only place the current query is visible without opening anything, and a bare
 * count tells the reader they have to open it to find out what they chose.
 */
export function summariseValues(values: string[]): string {
  if (values.length === 0) return 'Any';
  if (values.length === 1) return titleCase(values[0]);
  if (values.length === 2) return `${titleCase(values[0])}, ${titleCase(values[1])}`;
  return `${titleCase(values[0])} +${values.length - 1}`;
}
